<template>
  <DialogHeader>
    <template #left>
      <div class="font-medium text-gray-800 cursor-pointer" @click="close">
        Close
      </div>
    </template>
    <div>
      Create
      <span class="hidden tablet-h:inline-block">
        technical comparison analysis
      </span>
      report
    </div>
  </DialogHeader>
  <DialogContent>
    <div class="mb-[14px]">
      Select works to compare for infringement with
      <span
        class="w-fit bg-linear-to-r from-yellow-500 via-[#F84600] to-[#CE489A] bg-clip-text text-transparent font-medium"
      >
        Dacr AI
      </span>
    </div>

    <div
      v-if="loading"
      class="h-[200px] mx-auto flex flex-col justify-center items-center"
    >
      <CircularProgressIndicator
        v-if="uploading"
        :percentage="percentage"
        strokeColor="#34B1C7"
        class="mb-[18px] border-gray-200/30!"
      />

      <div v-if="uploading" class="w-full text-center" v-truncate>
        <div data-truncate-target>Uploading {{ file?.name }}</div>
      </div>
      <div
        v-else
        class="font-secondary text-[14px] leading-[18px] text-gray-800"
      >
        Processing
      </div>
    </div>

    <template v-else>
      <div class="mb-[14px]">
        <div class="text-[12px] leading-[16px] text-gray-500 mb-2">
          Your copyright on Dacr
        </div>

        <div
          v-if="copyright"
          :key="copyright.id"
          class="w-full h-[68px] rounded-xl border border-gray-200 pl-1.5 pr-[14px] flex items-center gap-x-2.5"
        >
          <ThumbnailIcon
            class="bg-gray-200/30 rounded-md tablet-h:min-w-[56px] tablet-h:max-w-[56px] tablet-h:h-[56px]"
            :icon-size="36"
            :type="copyright.type"
            :image-url="
              applyAuthorizationToken(copyright.asset?.previewImageUrl, [
                {
                  key: 'width',
                  value: 36,
                },
              ])
            "
          />

          <div class="w-full" v-truncate>
            <div data-truncate-target>{{ copyright.name }}</div>
          </div>

          <div
            class="font-medium text-green-700 hover:text-green-800 cursor-pointer"
            @click="handleSelectCopyrightClick"
          >
            Change
          </div>
        </div>

        <UiButton v-else class="w-full" @click="handleSelectCopyrightClick">
          Select copyright
        </UiButton>
      </div>

      <div
        class="mx-auto shdw size-7 border border-black/8 flex items-center justify-center rounded-full bg-white mb-[14px]"
      >
        <Icon name="Sort" :size="20" class="stroke-gray-800" />
      </div>

      <div>
        <div class="text-[12px] leading-[16px] text-gray-500 mb-2">
          Potential infringing file
        </div>

        <input
          ref="fileInput"
          type="file"
          class="hidden"
          @change="handleFileInput"
        />

        <div
          v-if="file"
          class="w-full h-[200px] border border-gray-200 rounded-lg flex items-center justify-center"
        >
          <div class="w-full flex flex-col items-center text-center">
            <ThumbnailIcon
              class="w-[90px] h-[90px] mb-2.5"
              :type="getFileType(file.name, file.type)"
              :icon-size="90"
              showShadow
              transition
            />

            <div class="mb-2.5 w-full" v-truncate>
              <div data-truncate-target>{{ file.name }}</div>
            </div>

            <div
              class="font-medium text-green-700 hover:text-green-800 cursor-pointer"
              @click="handleRemoveClick"
            >
              Remove
            </div>
          </div>
        </div>

        <div
          v-else
          class="w-full h-[200px] border-3 border-dashed border-red-200 rounded-lg bg-red-200/5 flex items-center justify-center cursor-pointer"
          :class="classes"
          @click="fileInput?.click()"
          @dragover.prevent="handleDragOver"
          @dragleave="handleDragLeave"
          @drop.prevent="handleDrop"
        >
          <div class="w-[200px] flex flex-col items-center text-center">
            <Icon name="File" :size="24" class="stroke-red-500 mb-[18px]" />

            <div class="mb-[18px] leading-[20px]">
              Drag and drop infringing file or
              <span class="text-red-500 font-medium">upload from computer</span>
            </div>

            <div class="text-gray-400">Max 10GB</div>
          </div>
        </div>
      </div>
    </template>

    <div class="mt-5">
      <Message v-if="loading" class="text-[12px!]" type="default" icon="Info">
        Keep your browser open until the upload is complete
      </Message>

      <div v-else class="w-full">
        <Message
          v-if="!disableButton"
          class="text-[12px]! mb-[14px]"
          type="default"
        >
          By selecting "Agree and pay" you are agreeing to our
          <span>
            <a class="text-link -mr-1" href="/legal/terms" target="_blank">
              Terms
            </a>
            ,
          </span>
          <a class="text-link" href="/legal/privacy" target="_blank">
            Privacy Policy
          </a>
          and
          <span>
            <a class="text-link -mr-1" href="/legal/pricing" target="_blank">
              Pricing Terms
            </a>
            .
          </span>
        </Message>

        <UiButton
          class="w-full! mb-1.5"
          :disabled="disableButton"
          primary
          @click="handleClick"
        >
          Create report
        </UiButton>

        <div
          v-if="!disableButton"
          class="font-secondary text-[12px] leading-[18px] text-center"
        >
          Creating this report will use 1 of your report credits
        </div>
      </div>
    </div>
  </DialogContent>
</template>

<script setup lang="ts">
import { Icon, Message, ThumbnailIcon, UiButton } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { computed, ref } from 'vue'
import { getFileType } from '@/shared/utils/file'
import type { File as GqlFile } from '@/shared/graphql/router/graphql'
import { applyAuthorizationToken } from '@/shared/utils/url'
import CircularProgressIndicator from '@/shared/components/CircularProgressIndicator.vue'
import { graphql } from '@/shared/graphql/router'
import { useMutation } from '@vue/apollo-composable'
import axios, { type AxiosProgressEvent } from 'axios'
import { useUserStore } from '@/shared/stores/user'

const { close, open } = useDialog()

const { mutate: uploadInfringingFile } = useMutation(
  graphql(`
    mutation UploadInfringingFile($input: UploadInfringingFileInput!) {
      uploadInfringingFile(input: $input) {
        id
        url
        headers
        error {
          code
          message
        }
      }
    }
  `),
  {
    clientId: 'router',
  }
)

const userStore = useUserStore()

const fileInput = ref<HTMLInputElement>()
const file = ref<File>()
const copyright = ref<GqlFile>()
const dragging = ref(false)
const loading = ref(false)
const uploading = ref(false)
const percentage = ref(0)

const classes = computed(() => ({
  'bg-red-200/20!': dragging.value,
}))

const disableButton = computed(() => !file.value || !copyright.value)

const handleDragOver = () => {
  dragging.value = true
}

const handleDragLeave = () => {
  dragging.value = false
}

const handleDrop = async (event: DragEvent) => {
  dragging.value = false

  const files = event.dataTransfer?.files

  if (!files?.length) return

  file.value = files[0]
}

const handleFileInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files?.length) {
    file.value = target.files[0]
  }
}

const handleRemoveClick = () => {
  file.value = undefined
}

const handleSelectCopyrightClick = () => {
  open(Dialogs.SelectCopyright, {
    stack: true,
    events: {
      success(item: GqlFile) {
        copyright.value = item
      },
    },
  })
}

const handleClick = async () => {
  if (!file.value || !copyright.value) return

  loading.value = true

  try {
    const uploadResponse = await uploadInfringingFile({
      input: {
        copyrightId: copyright.value.id,
        filename: file.value.name,
      },
    })

    const uploadData = uploadResponse?.data?.uploadInfringingFile

    if (!uploadData?.url || !uploadData?.id) {
      throw new Error(uploadData?.error?.message || 'Failed to get upload URL')
    }

    const headers = JSON.parse(uploadData.headers) as Record<string, string>
    const formData = new FormData()

    Object.entries(headers)
      .filter(([, value]) => !!value)
      .forEach(([key, value]) => {
        formData.append(key.replace(/_/g, '-'), value)
      })

    formData.append('file', file.value)

    uploading.value = true

    await axios.post(uploadData.url, formData, {
      onUploadProgress: (event: AxiosProgressEvent) => {
        percentage.value = Math.round((event.progress || 0) * 100)
      },
    })

    userStore.updateUser({ isCreatingReport: true })

    close()
  } catch (error) {
    console.error('[CreateReportDialog]', error)
    loading.value = false
    uploading.value = false
    percentage.value = 0
  }
}
</script>

<style scoped>
.shdw {
  box-shadow:
    0 4px 18px 0 rgba(0, 0, 0, 0.04),
    0 0.8px 2.93px 0 rgba(0, 0, 0, 0.02);
}
</style>
